import { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { fetchData } from '../../apiService';
import useSessionManager from '../../hooks/useSessionManager';
// import SaleModal from './Sale/SaleModal';
// import PurchaseModal from './Purchase/PurchaseModal';
import {
  TrendingUp, TrendingDown, AccountBalance, Inventory, ShoppingCart,
  Assessment, Warning, Receipt, Payment, Add, Refresh, ArrowForward,
  Schedule, LocalShipping, RequestQuote, SwapHoriz, AccountBalanceWallet,
  BarChart, PieChart, Notifications, PersonAdd, Storefront
} from '@mui/icons-material';
import { CircularProgress, Tooltip, IconButton } from '@mui/material';
import './Home.css';

const Home = () => {
  const { companyName, username, companyId, branchId, token } = useAuth();
  const { validateSession } = useSessionManager();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // ── Real KPI state ────────────────────────────────────────────────────────
  const [kpis, setKpis] = useState([
    { key: 'receivable', label: 'Accounts Receivable', value: '₹0', sub: [{ l: 'Overdue', v: '₹0', c: 'neg' }, { l: 'Credit Clients', v: '0' }, { l: 'Due Today', v: '₹0', c: 'warn' }], trend: '0%', up: true },
    { key: 'payable',    label: 'Accounts Payable',    value: '₹0', sub: [{ l: 'Due Today', v: '₹0', c: 'warn' }, { l: 'Overdue', v: '₹0', c: 'neg' }, { l: 'Active Suppliers', v: '0' }], trend: '0%', up: false },
    { key: 'revenue',    label: 'Frame Sales (Month)', value: '₹0', sub: [{ l: "Today's Sales", v: '₹0', c: 'pos' }, { l: 'Total Bills', v: '0' }, { l: 'Avg Bill', v: '₹0' }], trend: '0%', up: true },
    { key: 'purchase',   label: 'Purchases (Month)',   value: '₹0', sub: [{ l: "Today's Purchase", v: '₹0' }, { l: 'Total Bills', v: '0' }, { l: 'Pending POs', v: '0', c: 'warn' }], trend: '0%', up: false },
    { key: 'stock',      label: 'Frame Stock Value',   value: '₹0', sub: [{ l: 'Low Stock Frames', v: '0', c: 'warn' }, { l: 'Out of Stock', v: '0', c: 'neg' }, { l: 'Total Frame SKUs', v: '0' }], trend: '0%', up: true },
    { key: 'cashflow',   label: 'Net Cash Flow',       value: '₹0', sub: [{ l: 'Cash In', v: '₹0', c: 'pos' }, { l: 'Cash Out', v: '₹0', c: 'neg' }, { l: 'Balance', v: '₹0', c: 'pos' }], trend: '0%', up: true },
  ]);

  const [activities, setActivities] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [moduleStats, setModuleStats] = useState([
    { label: 'Bills',           value: 0, sub: '0 pending',    path: '/BillIndex',              color: 'ms-blue' },
    { label: 'Estimates',       value: 0, sub: '0 open',       path: '/EstimateIndex',          color: 'ms-orange' },
    { label: 'Payments',        value: 0, sub: 'this month',   path: '/PaymentAndReceiptIndex', color: 'ms-green' },
    { label: 'Credit/Debit',    value: 0, sub: '0 entries',    path: '/CreditDebitIndex',       color: 'ms-purple' },
    { label: 'Stock Transfers', value: 0, sub: '0 today',      path: '/StockTransferInIndex',   color: 'ms-teal' },
    { label: 'Journal Entries', value: 0, sub: 'this month',   path: '/JournalEntryIndex',      color: 'ms-indigo' },
  ]);
  const [receivableAging, setReceivableAging] = useState([
    { label: '0–30 Days',  value: 0, amount: '₹0', color: '#3b82f6' },
    { label: '31–60 Days', value: 0, amount: '₹0', color: '#10b981' },
    { label: '61–90 Days', value: 0, amount: '₹0', color: '#f59e0b' },
    { label: '90+ Days',   value: 0, amount: '₹0', color: '#ef4444' },
  ]);
  const [payableAging, setPayableAging] = useState([
    { label: '0–30 Days',  value: 0, amount: '₹0', color: '#3b82f6' },
    { label: '31–60 Days', value: 0, amount: '₹0', color: '#10b981' },
    { label: '61–90 Days', value: 0, amount: '₹0', color: '#f59e0b' },
    { label: '90+ Days',   value: 0, amount: '₹0', color: '#ef4444' },
  ]);

  // ── Monthly chart data ────────────────────────────────────────────────────
  const [chartData, setChartData] = useState({ sales: new Array(12).fill(0), purchases: new Array(12).fill(0) });
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // ── Activity View Modal ───────────────────────────────────────────────────
  const [viewModal, setViewModal] = useState({ open: false, type: null });
  const [viewFormData, setViewFormData] = useState({});
  const [viewDetails, setViewDetails] = useState([]);
  const [viewModalLoading, setViewModalLoading] = useState(false);
  const [viewSuppliers, setViewSuppliers] = useState([]);
  const [viewItems, setViewItems] = useState([]);
  const [viewBanks, setViewBanks] = useState([]);
  const [viewCustomers, setViewCustomers] = useState([]);

  // ── Raw reorder items (for Reorder button navigation) ────────────────────
  const reorderItemsRef = useRef([]);
  const [reorderLoading, setReorderLoading] = useState(false);

  // ── Module List Modal ─────────────────────────────────────────────────────
  const [moduleModal, setModuleModal] = useState({ open: false, label: '', data: [] });

  // ── Chart raw data (for bar click drill-down) ─────────────────────────────
  const [chartRawData, setChartRawData] = useState({ sales: [], purchases: [] });
  const [chartModal, setChartModal] = useState({ open: false, month: '', type: '', data: [] });

  // ── Quick Actions ─────────────────────────────────────────────────────────
  const quickActions = [
    { icon: <Receipt />,        label: 'New Bill',        path: '/BillIndex',              color: 'qa-blue' },
    { icon: <RequestQuote />,   label: 'New Estimate',    path: '/EstimateIndex',          color: 'qa-orange' },
    { icon: <Payment />,        label: 'Payment/Receipt', path: '/PaymentAndReceiptIndex', color: 'qa-green' },
    { icon: <AccountBalance />, label: 'Credit/Debit',    path: '/CreditDebitIndex',       color: 'qa-indigo' },
    { icon: <Inventory />,      label: 'Items',           path: '/ItemIndex',              color: 'qa-red' },
    { icon: <SwapHoriz />,      label: 'Frames',          path: '/FrameIndex',             color: 'qa-teal' },
    { icon: <PersonAdd />,      label: 'Masters',         path: '/MasterIndex',            color: 'qa-cyan' },
    { icon: <AccountBalance />, label: 'Banks',           path: '/BankIndex',              color: 'qa-purple' },
    { icon: <Storefront />,     label: 'Account Heads',   path: '/AccHeadIndex',           color: 'qa-amber' },
    { icon: <BarChart />,       label: 'Reports',         path: '/LedgerReport',           color: 'qa-pink' },
  ];

  // ── Helpers ───────────────────────────────────────────────────────────────
  const fmt = (n) => {
    if (!n || isNaN(n)) return '₹0';
    const num = parseFloat(n);
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000)   return `₹${(num / 100000).toFixed(2)} L`;
    if (num >= 1000) {
      const s = Math.round(num).toString();
      if (s.length > 3) return `₹${s.slice(0, s.length - 3)},${s.slice(-3)}`;
    }
    return `₹${Math.round(num)}`;
  };

  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  const yearStart  = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];

  const daysBetween = (dateStr) => {
    const d = new Date(dateStr);
    return Math.floor((today - d) / (1000 * 60 * 60 * 24));
  };

  // ── Data Fetching ─────────────────────────────────────────────────────────
  const loadDashboard = useCallback(async () => {
    if (!companyId || !token) return;
    try {
      // Fetch all data in parallel
      const [salesRes, purchasesRes, paymentRes, billsRes, estimatesRes, creditDebitRes, stockRes, itemsRes, clientsRes, vendorsRes, stockTransferRes, journalRes, debtorsRes, creditorsRes, cashBookRes] = await Promise.allSettled([
        fetchData('SaleApi',              `/ActiveList/${companyId}/${branchId}/${monthStart}/${todayStr}`, token),
        fetchData('PurchaseApi',          `/ActiveList/${companyId}/${branchId}/${monthStart}/${todayStr}`, token),
        fetchData('PaymentAndReceiptApi', `/ActiveList/${companyId}/${branchId}/${monthStart}/${todayStr}`, token),
        fetchData('BillApi',              `/BillList/${companyId}/${monthStart}/${todayStr}`, token),
        fetchData('EstimateApi',          `/EstimateList/${companyId}/${monthStart}/${todayStr}`, token),
        fetchData('CreditDebitApi',       `/All/${companyId}/${branchId}/${monthStart}/${todayStr}`, token),
        fetchData('SaleApi',              `/ItemStockListDetails/${companyId}/${branchId}`, token),
        fetchData('ItemApi',              `/${companyId}`, token),
        fetchData('ClientAndVendorApi',   `/ClientList/${companyId}`, token),
        fetchData('ClientAndVendorApi',   `/VendorList/${companyId}`, token),
        fetchData('StockInAndOutApi',     `/StockTranferInList/${companyId}/${branchId}/${monthStart}/${todayStr}`, token),
        fetchData('JournalEntryApi',      `/ActiveList/${companyId}/${branchId}/${monthStart}/${todayStr}`, token),
        fetchData('ReportApi',            `/DebtorsList/${companyId}/${branchId || 0}/${todayStr}/${todayStr}`, token),
        fetchData('ReportApi',            `/CreditorsList/${companyId}/${branchId || 0}/${todayStr}/${todayStr}`, token),
        fetchData('ReportApi',            `/CashBook/${companyId}/${branchId || 0}/${monthStart}/${todayStr}`, token),
      ]);

      const sales          = salesRes.status          === 'fulfilled' ? (salesRes.value.response_data          || salesRes.value          || []) : [];
      const purchases      = purchasesRes.status      === 'fulfilled' ? (purchasesRes.value.response_data      || purchasesRes.value      || []) : [];
      const payments       = paymentRes.status        === 'fulfilled' ? (paymentRes.value.response_data        || paymentRes.value        || []) : [];
      const bills          = billsRes.status          === 'fulfilled' ? (billsRes.value.response_data          || billsRes.value          || []) : [];
      const estimates      = estimatesRes.status      === 'fulfilled' ? (estimatesRes.value.response_data      || estimatesRes.value      || []) : [];
      const creditDebits   = creditDebitRes.status    === 'fulfilled' ? (creditDebitRes.value.response_data    || creditDebitRes.value    || []) : [];
      const stockList      = stockRes.status          === 'fulfilled' ? (stockRes.value.response_data          || stockRes.value          || []) : [];
      const allItems       = itemsRes.status          === 'fulfilled' ? (itemsRes.value.response_data          || itemsRes.value          || []) : [];
      const clients        = clientsRes.status        === 'fulfilled' ? (clientsRes.value.response_data        || clientsRes.value        || []) : [];
      const vendors        = vendorsRes.status        === 'fulfilled' ? (vendorsRes.value.response_data        || vendorsRes.value        || []) : [];
      const stockTransfers = stockTransferRes.status  === 'fulfilled' ? (stockTransferRes.value.response_data  || stockTransferRes.value  || []) : [];
      const journalEntries = journalRes.status        === 'fulfilled' ? (journalRes.value.response_data        || journalRes.value        || []) : [];
      const debtors        = debtorsRes.status        === 'fulfilled' ? (debtorsRes.value.response_data        || debtorsRes.value        || []) : [];
      const creditors      = creditorsRes.status      === 'fulfilled' ? (creditorsRes.value.response_data      || creditorsRes.value      || []) : [];
      const cashBookRows   = cashBookRes.status       === 'fulfilled' ? (cashBookRes.value.response_data       || cashBookRes.value       || []) : [];

      // ── Sales KPI ──────────────────────────────────────────────────────────
      const totalRevenue   = bills.reduce((s, i) => s + (parseFloat(i.TotalAmount) || 0), 0);
      const todaySales     = bills.filter(i => (i.BillDate || i.Date)?.split('T')[0] === todayStr).reduce((s, i) => s + (parseFloat(i.TotalAmount) || 0), 0);
      const avgBill        = bills.length ? totalRevenue / bills.length : 0;

      // ── Purchase KPI ──────────────────────────────────────────────────────
      const totalPurchase  = purchases.reduce((s, i) => s + (parseFloat(i.TotalAmount) || 0), 0);
      const todayPurchase  = purchases.filter(i => i.BillDate?.split('T')[0] === todayStr).reduce((s, i) => s + (parseFloat(i.TotalAmount) || 0), 0);
      const pendingPOs     = estimates.filter(i => !i.IsConverted).length;

      // ── Receivable / Payable ───────────────────────────────────────────────
      const totalReceivable = Array.isArray(debtors)
        ? debtors.reduce((s, i) => s + Math.abs(parseFloat(i.amount) || 0), 0)
        : bills.reduce((s, i) => s + (parseFloat(i.BalanceAmount) || 0), 0);
      const totalPayable = Array.isArray(creditors)
        ? creditors.reduce((s, i) => s + Math.abs(parseFloat(i.amount) || 0), 0)
        : purchases.reduce((s, i) => s + (parseFloat(i.BalanceAmount) || 0), 0);

      // Aging buckets for receivable — use debtors list (has `days` field)
      const rAmounts = [0, 0, 0, 0];
      if (Array.isArray(debtors) && debtors.length > 0) {
        debtors.forEach(i => {
          const amt = Math.abs(parseFloat(i.amount) || 0);
          if (amt <= 0) return;
          const age = parseInt(i.days) || 0;
          const idx = age <= 30 ? 0 : age <= 60 ? 1 : age <= 90 ? 2 : 3;
          rAmounts[idx] += amt;
        });
      } else {
        bills.forEach(i => {
          const bal = parseFloat(i.BalanceAmount) || 0;
          if (bal <= 0) return;
          const age = daysBetween(i.BillDate || i.Date);
          const idx = age <= 30 ? 0 : age <= 60 ? 1 : age <= 90 ? 2 : 3;
          rAmounts[idx] += bal;
        });
      }
      const rBuckets = [0, 0, 0, 0];
      const rTotal = rAmounts.reduce((a, b) => a + b, 0) || 1;
      rAmounts.forEach((v, i) => { rBuckets[i] = Math.round((v / rTotal) * 100); });

      // Aging buckets for payable — creditors list has only `amount`, use purchases for aging
      const pAmounts = [0, 0, 0, 0];
      purchases.forEach(i => {
        const bal = parseFloat(i.BalanceAmount) || 0;
        if (bal <= 0) return;
        const age = daysBetween(i.BillDate);
        const idx = age <= 30 ? 0 : age <= 60 ? 1 : age <= 90 ? 2 : 3;
        pAmounts[idx] += bal;
      });
      // If purchases have no balance data but creditors total exists, scale proportionally
      const pAmountsTotal = pAmounts.reduce((a, b) => a + b, 0);
      if (pAmountsTotal === 0 && totalPayable > 0) {
        pAmounts[0] = totalPayable; // put all in 0-30 if no aging detail
      }
      const pBuckets = [0, 0, 0, 0];
      const pTotal = pAmounts.reduce((a, b) => a + b, 0) || 1;
      pAmounts.forEach((v, i) => { pBuckets[i] = Math.round((v / pTotal) * 100); });

      setReceivableAging([
        { label: '0–30 Days',  value: rBuckets[0], amount: fmt(rAmounts[0]), color: '#3b82f6' },
        { label: '31–60 Days', value: rBuckets[1], amount: fmt(rAmounts[1]), color: '#10b981' },
        { label: '61–90 Days', value: rBuckets[2], amount: fmt(rAmounts[2]), color: '#f59e0b' },
        { label: '90+ Days',   value: rBuckets[3], amount: fmt(rAmounts[3]), color: '#ef4444' },
      ]);
      setPayableAging([
        { label: '0–30 Days',  value: pBuckets[0], amount: fmt(pAmounts[0]), color: '#3b82f6' },
        { label: '31–60 Days', value: pBuckets[1], amount: fmt(pAmounts[1]), color: '#10b981' },
        { label: '61–90 Days', value: pBuckets[2], amount: fmt(pAmounts[2]), color: '#f59e0b' },
        { label: '90+ Days',   value: pBuckets[3], amount: fmt(pAmounts[3]), color: '#ef4444' },
      ]);

      // ── Cash Flow (from CashBook) ──────────────────────────────────────────
      const cashIn  = cashBookRows.reduce((s, i) => s + Math.abs(parseFloat(i.debit)  || 0), 0);
      const cashOut = cashBookRows.reduce((s, i) => s + Math.abs(parseFloat(i.credit) || 0), 0);
      const netFlow = cashIn - cashOut;

      // ── Stock ──────────────────────────────────────────────────────────────
      const totalSKUs   = allItems.length;
      const lowStock    = stockList.filter(i => (parseFloat(i.CurrentStock) || 0) > 0 && (parseFloat(i.CurrentStock) || 0) <= (parseFloat(i.MinimumStock) || 0));
      const outOfStock  = stockList.filter(i => (parseFloat(i.CurrentStock) || 0) <= 0);
      const stockValue  = stockList.reduce((s, i) => s + ((parseFloat(i.CurrentStock) || 0) * (parseFloat(i.Rate || i.SaleRate) || 0)), 0);

      // Low stock items for widget
      const lowStockItems = [...outOfStock, ...lowStock].slice(0, 4).map(i => ({
        name:     i.ItemName || i.Name || 'Item',
        current:  Math.round(parseFloat(i.CurrentStock) || 0),
        min:      Math.round(parseFloat(i.MinimumStock) || 0),
        supplier: i.SupplierName || '-',
        pct:      i.MinimumStock > 0 ? Math.min(100, Math.round(((parseFloat(i.CurrentStock) || 0) / parseFloat(i.MinimumStock)) * 100)) : 0,
      }));
      setStockItems(lowStockItems);

      
      // Save raw items for Reorder navigation
      reorderItemsRef.current = [...outOfStock, ...lowStock].map(i => ({
        ItemId:   i.ItemId || i.Id,
        ItemName: i.ItemName || i.Name || '',
        HSNCode:  i.HSNCode || '',
        GSTRate:  parseFloat(i.GSTRate) || 0,
        Rate:     parseFloat(i.PurchaseRate || i.Rate || i.SaleRate) || 0,
      }));

      // ── KPIs ───────────────────────────────────────────────────────────────
      setKpis([
        { key: 'receivable', label: 'Accounts Receivable', value: fmt(totalReceivable),
          sub: [{ l: 'Overdue (90+d)', v: fmt(rAmounts[3]), c: 'neg' }, { l: 'Credit Clients', v: debtors.length || clients.filter(c => c.CreditLimit > 0).length || clients.length }, { l: 'Due (0-30d)', v: fmt(rAmounts[0]), c: 'warn' }],
          trend: `${debtors.length || bills.length} entries`, up: true },
        { key: 'payable', label: 'Accounts Payable', value: fmt(totalPayable),
          sub: [{ l: 'Due (0-30d)', v: fmt(pAmounts[0]), c: 'warn' }, { l: 'Overdue (90+d)', v: fmt(pAmounts[3]), c: 'neg' }, { l: 'Active Suppliers', v: creditors.length || vendors.length }],
          trend: `${creditors.length || purchases.length} entries`, up: false },
        { key: 'revenue', label: 'Frame Sales (Month)', value: fmt(totalRevenue),
          sub: [{ l: "Today's Sales", v: fmt(todaySales), c: 'pos' }, { l: 'Total Bills', v: bills.length }, { l: 'Avg Bill', v: fmt(avgBill) }],
          trend: `${bills.length} bills`, up: true },
        { key: 'purchase', label: 'Purchases (Month)', value: fmt(totalPurchase),
          sub: [{ l: "Today's Purchase", v: fmt(todayPurchase) }, { l: 'Total Bills', v: purchases.length }, { l: 'Pending Estimates', v: pendingPOs, c: pendingPOs > 0 ? 'warn' : '' }],
          trend: `${purchases.length} bills`, up: false },
        { key: 'stock', label: 'Frame Stock Value', value: fmt(stockValue),
          sub: [{ l: 'Low Stock Frames', v: lowStock.length, c: lowStock.length > 0 ? 'warn' : '' }, { l: 'Out of Stock', v: outOfStock.length, c: outOfStock.length > 0 ? 'neg' : '' }, { l: 'Total Frame SKUs', v: totalSKUs }],
          trend: `${totalSKUs} frames`, up: true },
        { key: 'cashflow', label: 'Net Cash Flow', value: fmt(netFlow),
          sub: [{ l: 'Cash In', v: fmt(cashIn), c: 'pos' }, { l: 'Cash Out', v: fmt(cashOut), c: 'neg' }, { l: 'Balance', v: fmt(netFlow), c: netFlow >= 0 ? 'pos' : 'neg' }],
          trend: netFlow >= 0 ? '+ve' : '-ve', up: netFlow >= 0 },
      ]);

      // ── Module Stats ───────────────────────────────────────────────────────
      const openEstimates  = estimates.filter(i => !i.IsConverted).length;
      const creditEntries  = creditDebits.filter(i => i.Type === 'Credit' || i.IsType === false || i.IsType === 0).length;
      const debitEntries   = creditDebits.filter(i => i.Type === 'Debit'  || i.IsType === true  || i.IsType === 1).length;
      setModuleStats([
        { label: 'Bills',           value: bills.length,          sub: `${bills.filter(i => !i.IsVerified).length} pending`,  path: '/BillIndex',              color: 'ms-blue',   listData: bills },
        { label: 'Estimates',       value: estimates.length,      sub: `${openEstimates} open`,                               path: '/EstimateIndex',          color: 'ms-orange', listData: estimates },
        { label: 'Payments',        value: payments.length,       sub: `this month`,                                          path: '/PaymentAndReceiptIndex', color: 'ms-green',  listData: payments },
        { label: 'Credit/Debit',    value: creditDebits.length,   sub: `${creditEntries} credit · ${debitEntries} debit`,     path: '/CreditDebitIndex',       color: 'ms-purple', listData: creditDebits },
        { label: 'Stock Transfers', value: stockTransfers.length, sub: `${stockTransfers.length} this month`,                 path: '/StockTransferInIndex',   color: 'ms-teal',   listData: stockTransfers },
        { label: 'Journal Entries', value: journalEntries.length, sub: 'this month',                                          path: '/JournalEntryIndex',      color: 'ms-indigo', listData: journalEntries },
      ]);

      // ── Recent Activities ──────────────────────────────────────────────────
      const recentBills = bills.slice(0, 3).map(i => ({
        type: 'sale', title: `Bill #${i.BillNumber || ''}`, party: i.ClientName || i.CustomerName || i.CistomerName || i.MasterName || '-',
        amount: fmt(i.TotalAmount), date: (i.BillDate || i.Date)?.split('T')[0] || '',
        status: i.IsVerified ? 'verified' : i.IsActive ? 'pending' : 'inactive',
        id: i.Id,
      }));
      const recentPurchases = purchases.slice(0, 2).map(i => ({
        type: 'purchase', title: `Purchase #${i.BillNumber || ''}`, party: i.MasterName || '-',
        amount: fmt(i.TotalAmount), date: i.BillDate?.split('T')[0] || '',
        status: i.IsVerified ? 'verified' : 'pending',
        id: i.Id,
      }));
      const recentPayments = payments.slice(0, 1).map(i => ({
        type: 'payment', title: `Payment #${i.BillNumber || ''}`, party: i.MasterName || '-',
        amount: fmt(i.Amount || i.TotalAmount), date: i.BillDate?.split('T')[0] || '',
        status: 'completed',
        id: i.Id,
      }));
      setActivities([...recentBills, ...recentPurchases, ...recentPayments].slice(0, 6));

      // ── Alerts ─────────────────────────────────────────────────────────────
      const newAlerts = [];
      const overdueReceivables = bills.filter(i => (parseFloat(i.BalanceAmount) || 0) > 0 && daysBetween(i.BillDate || i.Date) > 30);
      if (overdueReceivables.length > 0) {
        const top = overdueReceivables[0];
        newAlerts.push({ type: 'overdue', title: 'Overdue Invoice', detail: `${top.ClientName || top.CustomerName || top.CistomerName || top.MasterName || 'Customer'} — ${fmt(top.BalanceAmount)} overdue by ${daysBetween(top.BillDate || top.Date)} days`, action: 'Send Reminder' });
      }
      const duePayables = purchases.filter(i => (parseFloat(i.BalanceAmount) || 0) > 0 && daysBetween(i.BillDate) <= 7);
      if (duePayables.length > 0) {
        const top = duePayables[0];
        newAlerts.push({ type: 'payment', title: 'Vendor Payment Due', detail: `${top.MasterName || 'Vendor'} — ${fmt(top.BalanceAmount)} due soon`, action: 'Pay Now', masterId: top.MasterId, masterName: top.MasterName, amount: top.BalanceAmount });
      }
      if (lowStock.length > 0) {
        newAlerts.push({ type: 'stock', title: 'Low Frame Stock Alert', detail: `${lowStock[0].ItemName || 'Frame'} — only ${Math.round(parseFloat(lowStock[0].CurrentStock) || 0)} units remaining`, action: 'Reorder' });
      }
      if (outOfStock.length > 0) {
        newAlerts.push({ type: 'stock', title: 'Frames Out of Stock', detail: `${outOfStock.length} frame(s) out of stock`, action: 'Reorder' });
      }
      if (openEstimates > 0) {
        newAlerts.push({ type: 'po', title: 'Open Estimates', detail: `${openEstimates} Estimate(s) not yet converted`, action: 'Review' });
      }
      setAlerts(newAlerts.slice(0, 5));

      // ── Monthly Chart ──────────────────────────────────────────────────────
      // Fetch full year data for chart
      const [yBillsRes, yPurchasesRes] = await Promise.allSettled([
        fetchData('BillApi',     `/BillList/${companyId}/${yearStart}/${todayStr}`, token),
        fetchData('PurchaseApi', `/ActiveList/${companyId}/${branchId}/${yearStart}/${todayStr}`, token),
      ]);
      const yBills     = yBillsRes.status     === 'fulfilled' ? (yBillsRes.value.response_data     || yBillsRes.value     || []) : [];
      const yPurchases = yPurchasesRes.status === 'fulfilled' ? (yPurchasesRes.value.response_data || yPurchasesRes.value || []) : [];

      const monthlySales     = new Array(12).fill(0);
      const monthlyPurchases = new Array(12).fill(0);
      yBills.forEach(i => {
        const m = new Date(i.BillDate || i.Date).getMonth();
        if (!isNaN(m)) monthlySales[m] += parseFloat(i.TotalAmount) || 0;
      });
      yPurchases.forEach(i => {
        const m = new Date(i.BillDate).getMonth();
        if (!isNaN(m)) monthlyPurchases[m] += parseFloat(i.TotalAmount) || 0;
      });
      setChartData({ sales: monthlySales, purchases: monthlyPurchases });
      setChartRawData({ sales: yBills, purchases: yPurchases });

    } catch (err) {
      console.error('Dashboard load error:', err);
    }
  }, [companyId, branchId, token]);

  useEffect(() => {
    if (!validateSession()) return;
    setLoading(true);
    loadDashboard().finally(() => setLoading(false));
  }, [validateSession, loadDashboard]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadDashboard().finally(() => setRefreshing(false));
  };

  // ── Activity click → open view modal ─────────────────────────────────────
  const handleActivityClick = async (activity) => {
    if (!activity.id || activity.type === 'payment') return;
    setViewModalLoading(true);
    setViewModal({ open: true, type: activity.type });

    try {
      if (activity.type === 'sale') {
        const [res, customersRes, banksRes] = await Promise.allSettled([
          fetchData('BillApi', `/${activity.id}`, token),
          viewCustomers.length ? Promise.resolve({ response_data: viewCustomers }) : fetchData('ClientAndVendorApi', `/ClientList/${companyId}`, token),
          viewBanks.length ? Promise.resolve({ response_data: viewBanks }) : fetchData('BankApi', `/${companyId}`, token),
        ]);
        const freshData = res.status === 'fulfilled' ? (res.value.response_data || res.value) : null;
        if (freshData) {
          const saleData = freshData.Sale || freshData;
          const details = freshData.SaleDetails || [];
          setViewFormData({
            Id: saleData.Id, CompanyId: saleData.CompanyId, BranchId: saleData.BranchId,
            MasterId: saleData.MasterId, CustomerName: saleData.CustomerName || '',
            InvoiceTypeId: saleData.InvoiceTypeId || 0, InvoiceTypeName: saleData.InvoiceTypeName || '',
            BillNumber: saleData.BillNumber, BillPrefix: saleData.BillPrefix || '',
            FinYear: saleData.FinYear || '', BillDate: saleData.BillDate?.split('T')[0] || '',
            ClearingDate: saleData.ClearingDate?.split('T')[0] || '',
            GstCalculationType: saleData.GstCalculationType || 1,
            GstCalculationTypeName: saleData.GstCalculationTypeName || 'Excluding Tax',
            Amount: saleData.Amount || 0, DiscountAmount: saleData.DiscountAmount || 0,
            TaxableAmount: saleData.TaxableAmount || 0, CGSTAmount: saleData.CGSTAmount || 0,
            SGSTAmount: saleData.SGSTAmount || 0, IGSTAmount: saleData.IGSTAmount || 0,
            FreightAmount: saleData.FreightAmount || 0, FreightGSTRate: saleData.FreightGSTRate || 0,
            FreightGSTAmount: saleData.FreightGSTAmount || 0, PackingAmount: saleData.PackingAmount || 0,
            PackingGSTRate: saleData.PackingGSTRate || 0, PackingGSTAmount: saleData.PackingGSTAmount || 0,
            TCSAmount: saleData.TCSAmount || 0, RoundOff: saleData.RoundOff || 0,
            TotalAmount: saleData.TotalAmount || saleData.GrandTotal || 0,
            GrandTotal: saleData.GrandTotal || saleData.TotalAmount || 0,
            CashAmount: saleData.CashAmount || 0, UPIAmount: saleData.UPIAmount || 0,
            CardAmount: saleData.CardAmount || 0, BankId: saleData.BankId || null,
            BalanceAmount: saleData.BalanceAmount || 0, Remarks: saleData.Remarks || '',
            DispatchFrom: saleData.DispatchFrom || '', DispatchAddress1: saleData.DispatchAddress1 || '',
            DispatchAddress2: saleData.DispatchAddress2 || '', DispatchLocation: saleData.DispatchLocation || '',
            DispatchPincode: saleData.DispatchPincode || '', ShipTo: saleData.ShipTo || '',
            ShipGSTNo: saleData.ShipGSTNo || '', ShipToAddress1: saleData.ShipToAddress1 || '',
            ShipToAddress2: saleData.ShipToAddress2 || '', ShipToLocation: saleData.ShipToLocation || '',
            ShipToPincode: saleData.ShipToPincode || '', VehicleNumber: saleData.VehicleNumber || '',
            TransporterName: saleData.TransporterName || '', TransporterGST: saleData.TransporterGST || '',
            Distance: saleData.Distance || 0, IsVerified: saleData.IsVerified || false,
            IsActive: saleData.IsActive ?? true,
          });
          setViewDetails(details.map(d => ({
            Id: d.Id || 0, ItemId: d.ItemId, ItemName: d.ItemName || '',
            HSNCode: d.HSNCode || '', GSTRate: d.GSTRate || 0, Quantity: d.Quantity || 0,
            Rate: d.Rate || 0, Amount: d.Amount || 0, DiscountPercentage: d.DiscountPercentage || 0,
            DiscountAmount: d.DiscountAmount || 0, TaxableAmount: d.TaxableAmount || 0,
            CGSTAmount: d.CGSTAmount || 0, SGSTAmount: d.SGSTAmount || 0, IGSTAmount: d.IGSTAmount || 0,
            TotalAmount: d.TotalAmount || 0, IsActive: d.IsActive ?? true,
          })));
        }
        if (customersRes.status === 'fulfilled' && !viewCustomers.length) {
          setViewCustomers(customersRes.value.response_data || customersRes.value || []);
        }
        if (banksRes.status === 'fulfilled' && !viewBanks.length) {
          setViewBanks(banksRes.value.response_data || banksRes.value || []);
        }
      } else if (activity.type === 'purchase') {
        const [res, suppliersRes, itemsRes] = await Promise.allSettled([
          fetchData('PurchaseApi', `/${activity.id}`, token),
          viewSuppliers.length ? Promise.resolve({ response_data: viewSuppliers }) : fetchData('ClientAndVendorApi', `/VendorList/${companyId}`, token),
          viewItems.length ? Promise.resolve({ response_data: viewItems }) : fetchData('ItemApi', `/${companyId}`, token),
        ]);
        const freshData = res.status === 'fulfilled' ? (res.value.response_data || res.value) : null;
        if (freshData) {
          const p = freshData.Purchase || freshData;
          const details = freshData.PurchaseDetails || [];
          setViewFormData({
            Id: p.Id, CompanyId: p.CompanyId, BranchId: p.BranchId,
            MasterId: p.MasterId, MasterName: p.MasterName || '',
            InvoiceTypeId: p.InvoiceTypeId || 0, InvoiceTypeName: p.InvoiceTypeName || '',
            GSTNumber: p.GSTNumber || '', BillDate: p.BillDate?.split('T')[0] || '',
            ClearingDate: p.ClearingDate?.split('T')[0] || '', BillNumber: p.BillNumber || '',
            GstCalculationType: p.GstCalculationType || 1,
            GstCalculationTypeName: p.GstCalculationTypeName || 'Excluding Tax',
            Amount: p.Amount || 0, DiscountAmount: p.DiscountAmount || 0,
            TaxableAmount: p.TaxableAmount || 0, CGSTAmount: p.CGSTAmount || 0,
            SGSTAmount: p.SGSTAmount || 0, IGSTAmount: p.IGSTAmount || 0,
            FreightAmount: p.FreightAmount || 0, FreightGSTRate: p.FreightGSTRate || 0,
            FreightGSTAmount: p.FreightGSTAmount || 0, PackingAmount: p.PackingAmount || 0,
            PackingGSTRate: p.PackingGSTRate || 0, PackingGSTAmount: p.PackingGSTAmount || 0,
            TCSAmount: p.TCSAmount || 0, RoundOff: p.RoundOff || 0,
            TotalAmount: p.TotalAmount || 0, PaidAmount: p.PaidAmount || 0,
            BalanceAmount: p.BalanceAmount || 0, Remarks: p.Remarks || '',
            IsActive: p.IsActive ?? true, IsVerified: p.IsVerified ?? false,
          });
          setViewDetails(details.map(d => ({
            Id: d.Id || 0, ItemId: d.ItemId, ItemName: d.ItemName || '',
            HSNCode: d.HSNCode || '', GSTRate: d.GSTRate || 0,
            ReceivedQuantity: d.ReceivedQuantity || 0, Rate: d.Rate || 0,
            Amount: d.Amount || 0, DiscountPercentage: d.DiscountPercentage || 0,
            DiscountAmount: d.DiscountAmount || 0, TaxableAmount: d.TaxableAmount || 0,
            CGSTAmount: d.CGSTAmount || 0, SGSTAmount: d.SGSTAmount || 0, IGSTAmount: d.IGSTAmount || 0,
            TotalAmount: d.TotalAmount || 0, IsActive: d.IsActive ?? true,
          })));
        }
        if (suppliersRes.status === 'fulfilled' && !viewSuppliers.length) {
          setViewSuppliers(suppliersRes.value.response_data || suppliersRes.value || []);
        }
        if (itemsRes.status === 'fulfilled' && !viewItems.length) {
          setViewItems(itemsRes.value.response_data || itemsRes.value || []);
        }
      }
    } catch (err) {
      console.error('Activity click error:', err);
    } finally {
      setViewModalLoading(false);
    }
  };

  const handleCloseViewModal = () => {
    setViewModal({ open: false, type: null });
    setViewFormData({});
    setViewDetails([]);
  };

  const handleReorder = () => {
    setReorderLoading(true);
    setTimeout(() => {
      history.push('/EstimateIndex', { reorderItems: reorderItemsRef.current });
    }, 300);
  };

  const formatPct = (pct) => {
    const color = pct <= 20 ? '#ef4444' : pct <= 40 ? '#f59e0b' : '#10b981';
    return { width: `${Math.min(pct, 100)}%`, background: color };
  };

  const maxVal = Math.max(...chartData.sales, ...chartData.purchases, 1);

  if (loading) {
    return (
      <div style={{ minHeight: 'calc(100vh - 48px)', background: '#f0f4f8', position: 'relative' }}>
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(240, 244, 248, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, gap: 36,
        }}>
        {/* Logo blocks — 3D flip reveal in sequence */}
        <div style={{ position: 'relative', width: 90, height: 74, perspective: 400 }}>
          {/* Green */}
          <div style={{
            position: 'absolute', width: 36, height: 36, background: '#3aaa5c',
            borderRadius: 6, bottom: 0, left: 0,
            animation: 'tileFlip 2s ease-in-out infinite',
            animationDelay: '0s',
            boxShadow: '0 0 24px rgba(58,170,92,0.6)',
          }} />
          {/* Blue */}
          <div style={{
            position: 'absolute', width: 30, height: 52, background: '#2d6db5',
            borderRadius: 6, bottom: 0, left: 30,
            animation: 'tileFlip 2s ease-in-out infinite',
            animationDelay: '0.3s',
            boxShadow: '0 0 24px rgba(45,109,181,0.6)',
          }} />
          {/* Red */}
          <div style={{
            position: 'absolute', width: 34, height: 34, background: '#cc2b2b',
            borderRadius: 6, top: 0, right: 0,
            animation: 'tileFlip 2s ease-in-out infinite',
            animationDelay: '0.6s',
            boxShadow: '0 0 24px rgba(204,43,43,0.6)',
          }} />
          {/* Orange */}
          <div style={{
            position: 'absolute', width: 34, height: 34, background: '#e8a020',
            borderRadius: 6, bottom: 0, right: 0,
            animation: 'tileFlip 2s ease-in-out infinite',
            animationDelay: '0.9s',
            boxShadow: '0 0 24px rgba(232,160,32,0.6)',
          }} />
        </div>

        {/* Company name with shimmer */}
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: 24, fontWeight: 800, color: 'transparent',
            letterSpacing: 3, textTransform: 'uppercase',
            fontFamily: 'Verdana, sans-serif',
            background: 'linear-gradient(90deg, #3aaa5c, #2d6db5, #cc2b2b, #e8a020, #3aaa5c)',
            backgroundSize: '300% auto',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: 'shimmerText 2.5s linear infinite',
          }}>            {companyName || 'ERP'}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(30,40,80,0.5)', marginTop: 8, letterSpacing: 2, textTransform: 'uppercase' }}>
            Loading Frame Dashboard
            <span style={{ animation: 'dots 1.5s steps(3,end) infinite' }}>...</span>
          </div>
        </div>

        {/* Dot indicators */}
        <div style={{ display: 'flex', gap: 8 }}>
          {['#3aaa5c','#2d6db5','#cc2b2b','#e8a020'].map((c, i) => (
            <div key={i} style={{
              width: 8, height: 8, borderRadius: '50%', background: c,
              animation: 'dotPulse 1.2s ease-in-out infinite',
              animationDelay: `${i * 0.2}s`,
            }} />
          ))}
        </div>

        <style>{`
          @keyframes tileFlip {
            0%   { transform: rotateY(0deg) scale(1); opacity: 1; }
            25%  { transform: rotateY(90deg) scale(0.8); opacity: 0.3; }
            50%  { transform: rotateY(180deg) scale(1); opacity: 1; }
            75%  { transform: rotateY(270deg) scale(0.8); opacity: 0.3; }
            100% { transform: rotateY(360deg) scale(1); opacity: 1; }
          }
          @keyframes shimmerText {
            0%   { background-position: 0% center; }
            100% { background-position: 300% center; }
          }
          @keyframes dotPulse {
            0%, 100% { transform: scale(1); opacity: 0.4; }
            50%       { transform: scale(1.6); opacity: 1; }
          }
          @keyframes dots {
            0%   { content: '.'; }
            33%  { content: '..'; }
            66%  { content: '...'; }
          }
        `}</style>
        </div>
      </div>
    );
  }

  return (
    <div className="dash">
      {/* ── Top Bar ─────────────────────────────────────────────────────── */}
      {/*<div className="dash-topbar">
        <div className="dash-topbar-left">
          <h2 className="dash-title">Dashboard</h2>
          <span className="dash-subtitle">Welcome back, {username || 'User'}</span>
        </div>
        <div className="dash-topbar-right">
          <div className="dash-date-badge">
            {new Date().toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
          </div>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} size="small" className="dash-refresh-btn">
              <Refresh className={refreshing ? 'spinning' : ''} />
            </IconButton>
          </Tooltip>
        </div>
      </div>*/}

      {/* ── KPI Cards ───────────────────────────────────────────────────── */}
      <div className="dash-kpi-grid">
        {kpis.map((k) => (
          <div key={k.key} className={`dash-kpi-card kpi-${k.key}`}>
            <div className="kpi-top">
              <div className={`kpi-icon kpi-icon-${k.key}`}>
                {k.key === 'receivable' && <AccountBalance />}
                {k.key === 'payable'    && <Payment />}
                {k.key === 'revenue'    && <TrendingUp />}
                {k.key === 'purchase'   && <ShoppingCart />}
                {k.key === 'stock'      && <Inventory />}
                {k.key === 'cashflow'   && <Assessment />}
              </div>
              <div className="kpi-meta">
                <span className="kpi-label">{k.label}</span>
                <span className={`kpi-trend ${k.up ? 'trend-up' : 'trend-down'}`}>
                  {k.up ? <TrendingUp /> : <TrendingDown />} {k.trend}
                </span>
              </div>
            </div>
            <div className="kpi-value">{k.value}</div>
            <div className="kpi-subs">
              {k.sub.map((s, i) => (
                <div key={i} className="kpi-sub-row">
                  <span className="kpi-sub-label">{s.l}</span>
                  <span className={`kpi-sub-val ${s.c === 'pos' ? 'c-pos' : s.c === 'neg' ? 'c-neg' : s.c === 'warn' ? 'c-warn' : ''}`}>{s.v}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* ── Main Grid ───────────────────────────────────────────────────── */}
      <div className="dash-main-grid">

        {/* ── Left Column ─────────────────────────────────────────────── */}
        <div className="dash-col-left">
          <div className="dash-card">
            <div className="card-hdr">
              <span className="card-hdr-title"><Add fontSize="small" /> Quick Actions</span>
            </div>
            <div className="qa-grid">
              {quickActions.map((a, i) => (
                <button key={i} className={`qa-btn ${a.color}`} onClick={() => history.push(a.path)}>
                  <span className="qa-icon">{a.icon}</span>
                  <span className="qa-label">{a.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="dash-card">
            <div className="card-hdr">
              <span className="card-hdr-title"><BarChart fontSize="small" /> Module Overview</span>
            </div>
            <div className="module-grid">
              {moduleStats.map((m, i) => (
                <div key={i} className={`module-stat ${m.color}`}
                  onClick={() => m.listData?.length > 0
                    ? setModuleModal({ open: true, label: m.label, data: m.listData })
                    : history.push(m.path)
                  }
                  style={{ cursor: 'pointer' }}
                >
                  <span className="module-val">{m.value}</span>
                  <span className="module-label">{m.label}</span>
                  <span className="module-sub">{m.sub}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Center Column ───────────────────────────────────────────── */}
        <div className="dash-col-center">
          <div className="dash-card chart-card-tall">
            <div className="card-hdr">
              <span className="card-hdr-title"><BarChart fontSize="small" /> Frame Sales vs Purchases (Monthly)</span>
              <div className="chart-legend-row">
                <span className="cl-dot" style={{ background: '#2c3e6e' }}></span><span>Sales</span>
                <span className="cl-dot" style={{ background: '#e67e22' }}></span><span>Purchases</span>
              </div>
            </div>
            <div className="bar-chart-wrap">
              <div className="bar-chart-inner">
              {months.map((m, i) => (
                <div key={i} className="bar-col">
                  <div className="bar-pair">
                    <Tooltip title={`Sales: ${fmt(chartData.sales[i])}`}>
                      <div className="bar bar-sales"
                        style={{ height: `${(chartData.sales[i] / maxVal) * 100}%`, cursor: 'pointer' }}
                        onClick={() => {
                          const data = chartRawData.sales.filter(r => new Date(r.BillDate).getMonth() === i);
                          setChartModal({ open: true, month: m, type: 'Sales', data });
                        }}
                      ></div>
                    </Tooltip>
                    <Tooltip title={`Purchase: ${fmt(chartData.purchases[i])}`}>
                      <div className="bar bar-purchase"
                        style={{ height: `${(chartData.purchases[i] / maxVal) * 100}%`, cursor: 'pointer' }}
                        onClick={() => {
                          const data = chartRawData.purchases.filter(r => new Date(r.BillDate).getMonth() === i);
                          setChartModal({ open: true, month: m, type: 'Purchases', data });
                        }}
                      ></div>
                    </Tooltip>
                  </div>
                  <span className="bar-month">{m}</span>
                </div>
              ))}
              </div>
            </div>
          </div>

          <div className="dash-two-col">
            {/* Recent Activities */}
            <div className="dash-card">
              <div className="card-hdr">
                <span className="card-hdr-title"><Schedule fontSize="small" /> Recent Activities</span>
                <button className="card-hdr-link" onClick={() => history.push('/BillIndex')}>View All <ArrowForward fontSize="inherit" /></button>
              </div>
              <div className="activity-list">
                {activities.length === 0 && <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', padding: '16px 0' }}>No recent activities</div>}
                {activities.map((a, i) => (
                  <div key={i} className="activity-row"
                    onClick={() => handleActivityClick(a)}
                    style={{ cursor: a.type !== 'payment' ? 'pointer' : 'default' }}
                  >
                    <div className={`act-icon act-${a.type}`}>
                      {a.type === 'sale'     && <Receipt />}
                      {a.type === 'payment'  && <Payment />}
                      {a.type === 'purchase' && <ShoppingCart />}
                      {a.type === 'po'       && <LocalShipping />}
                      {a.type === 'receipt'  && <AccountBalanceWallet />}
                    </div>
                    <div className="act-info">
                      <span className="act-title">{a.title}</span>
                      <span className="act-party">{a.party}</span>
                    </div>
                    <div className="act-right">
                      <span className="act-amount">{a.amount}</span>
                      <span className={`act-status status-${a.status}`}>{a.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Alerts */}
            <div className="dash-card">
              <div className="card-hdr">
                <span className="card-hdr-title"><Notifications fontSize="small" /> Alerts</span>
                {alerts.length > 0 && <span className="alert-badge">{alerts.length}</span>}
              </div>
              <div className="alert-list">
                {alerts.length === 0 && <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', padding: '16px 0' }}>No alerts — all clear!</div>}
                {alerts.map((a, i) => (
                  <div key={i} className={`alert-row alert-${a.type}`}>
                    <div className="alert-icon-wrap">
                      {a.type === 'overdue' && <Warning />}
                      {a.type === 'payment' && <Payment />}
                      {a.type === 'stock'   && <Inventory />}
                      {a.type === 'po'      && <LocalShipping />}
                    </div>
                    <div className="alert-body">
                      <span className="alert-title">{a.title}</span>
                      <span className="alert-detail">{a.detail}</span>
                    </div>
                    <button className="alert-action" onClick={
                      a.type === 'stock' ? handleReorder :
                      a.type === 'payment' ? () => history.push('/PaymentAndReceiptIndex', { openCreate: true, masterId: a.masterId, masterName: a.masterName, amount: a.amount }) :
                      a.type === 'overdue' ? () => history.push('/PaymentAndReceiptIndex', { openCreate: true }) :
                      a.type === 'po' ? () => history.push('/EstimateIndex') :
                      undefined
                    } disabled={a.type === 'stock' && reorderLoading}>
                      {a.type === 'stock' && reorderLoading
                        ? <CircularProgress size={10} sx={{ color: 'inherit' }} />
                        : a.action}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Right Column ────────────────────────────────────────────── */}
        <div className="dash-col-right">
          <div className="dash-card">
            <div className="card-hdr">
              <span className="card-hdr-title"><PieChart fontSize="small" /> Receivable Aging</span>
              <span className="card-hdr-total">{kpis.find(k => k.key === 'receivable')?.value}</span>
            </div>
            <div className="aging-section">
              {receivableAging.map((a, i) => (
                <div key={i} className="aging-row">
                  <span className="aging-label">{a.label}</span>
                  <div className="aging-bar-track">
                    <div className="aging-bar-fill" style={{ width: `${a.value}%`, background: a.color }}></div>
                  </div>
                  <span className="aging-pct" style={{ color: a.color }}>{a.value}%</span>
                  <span className="aging-amt">{a.amount}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="dash-card">
            <div className="card-hdr">
              <span className="card-hdr-title"><PieChart fontSize="small" /> Payable Aging</span>
              <span className="card-hdr-total">{kpis.find(k => k.key === 'payable')?.value}</span>
            </div>
            <div className="aging-section">
              {payableAging.map((a, i) => (
                <div key={i} className="aging-row">
                  <span className="aging-label">{a.label}</span>
                  <div className="aging-bar-track">
                    <div className="aging-bar-fill" style={{ width: `${a.value}%`, background: a.color }}></div>
                  </div>
                  <span className="aging-pct" style={{ color: a.color }}>{a.value}%</span>
                  <span className="aging-amt">{a.amount}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="dash-card">
            <div className="card-hdr">
              <span className="card-hdr-title"><Warning fontSize="small" /> Low Frame Stock</span>
              <button className="card-hdr-link" onClick={() => history.push('/ItemIndex')}>View All <ArrowForward fontSize="inherit" /></button>
            </div>
            <div className="stock-list">
              {stockItems.length === 0 && <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', padding: '16px 0' }}>No low stock frames</div>}
              {stockItems.map((s, i) => (
                <div key={i} className="stock-row">
                  <div className="stock-info">
                    <span className="stock-name">{s.name}</span>
                    <span className="stock-supplier">{s.supplier}</span>
                  </div>
                  <div className="stock-bar-wrap">
                    <div className="stock-bar-track">
                      <div className="stock-bar-fill" style={formatPct(s.pct)}></div>
                    </div>
                    <span className="stock-qty">{s.current}/{s.min}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Sale View Modal ──────────────────────────────────────────────── */}
      {/* SaleModal removed - component not available */}

      {/* ── Purchase View Modal ──────────────────────────────────────────── */}
      {/* PurchaseModal removed - component not available */}
      {/* ── Module List Modal ────────────────────────────────────────────── */}
      {moduleModal.open && (
        <div style={{ position:'fixed',inset:0,background:'rgba(15,23,42,0.55)',backdropFilter:'blur(4px)',zIndex:1300,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px' }}
          onClick={() => setModuleModal({ open:false,label:'',data:[] })}>
          <div style={{ background:'#fff',borderRadius:16,width:'100%',maxWidth:580,maxHeight:'78vh',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.22)',overflow:'hidden' }}
            onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ background:'linear-gradient(135deg,#1e3a5f 0%,#2c3e6e 100%)',padding:'14px 18px',display:'flex',alignItems:'center',justifyContent:'space-between' }}>
              <div>
                <div style={{ color:'#fff',fontWeight:700,fontSize:15,letterSpacing:0.2 }}>{moduleModal.label}</div>
                <div style={{ color:'rgba(255,255,255,0.6)',fontSize:11,marginTop:2 }}>{moduleModal.data.length} records</div>
              </div>
              <button onClick={() => setModuleModal({ open:false,label:'',data:[] })}
                style={{ background:'rgba(255,255,255,0.15)',border:'none',borderRadius:8,width:28,height:28,cursor:'pointer',color:'#fff',fontSize:16,display:'flex',alignItems:'center',justifyContent:'center' }}>×</button>
            </div>
            {/* Table */}
            <div style={{ overflowY:'auto',flex:1 }}>
              <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12 }}>
                <thead>
                  <tr style={{ background:'#f8fafc',position:'sticky',top:0,zIndex:1 }}>
                    {['#','Party','Bill No','Date','Amount',...(moduleModal.label==='Journal Entries'||moduleModal.label==='Stock Transfers'?[]:['Status'])].map((h,i) => (
                      <th key={i} style={{ padding:'8px 12px',textAlign:i>=4?'right':'left',color:'#94a3b8',fontWeight:600,fontSize:11,textTransform:'uppercase',letterSpacing:0.5,borderBottom:'1px solid #e2e8f0',whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {moduleModal.data.map((row, idx) => (
                    <tr key={idx} style={{ borderBottom:'1px solid #f1f5f9',transition:'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background=''}>
                      <td style={{ padding:'7px 12px',color:'#cbd5e1',fontSize:11,width:28 }}>{idx+1}</td>
                      <td style={{ padding:'7px 12px',color:'#1e293b',fontWeight:600,maxWidth:140,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>
                        {row.MasterName||row.ClientName||row.CustomerName||row.VendorName||'-'}
                      </td>
                      <td style={{ padding:'7px 12px',color:'#475569',fontFamily:'monospace',fontSize:12 }}>
                        {row.PurchaseOrderNo||(row.BillPrefix||''+(row.BillNumber||row.BillNo||'-'))}
                      </td>
                      <td style={{ padding:'7px 12px',color:'#64748b',whiteSpace:'nowrap' }}>
                        {(row.PurchaseOrderDate||row.BillDate||'').split('T')[0]||'-'}
                      </td>
                      <td style={{ padding:'7px 12px',textAlign:'right',color:'#0f172a',fontWeight:700 }}>
                        {fmt(row.TotalAmount||row.GrandTotal||row.Amount||0)}
                      </td>
                      {moduleModal.label!=='Journal Entries'&&moduleModal.label!=='Stock Transfers'&&(
                      <td style={{ padding:'7px 12px',textAlign:'right' }}>
                        <span style={{ fontSize:10,padding:'2px 7px',borderRadius:20,fontWeight:700,
                          background:row.IsConverted?'#dcfce7':row.IsVerified?'#dbeafe':'#fef9c3',
                          color:row.IsConverted?'#15803d':row.IsVerified?'#1d4ed8':'#b45309' }}>
                          {row.IsConverted?'Converted':row.IsVerified?'Verified':'Pending'}
                        </span>
                      </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Footer */}
            <div style={{ padding:'10px 16px',borderTop:'1px solid #f0f0f0',display:'flex',justifyContent:'flex-end',gap:8,background:'#fafafa' }}>
              <button onClick={() => { setModuleModal({open:false,label:'',data:[]}); history.push(moduleStats.find(m=>m.label===moduleModal.label)?.path||'/'); }}
                style={{ padding:'6px 14px',borderRadius:8,border:'1px solid #e2e8f0',background:'#fff',cursor:'pointer',fontSize:12,color:'#475569',fontWeight:600 }}>
                View All
              </button>
              <button onClick={() => setModuleModal({open:false,label:'',data:[]})}
                style={{ padding:'6px 14px',borderRadius:8,border:'none',background:'linear-gradient(135deg,#1e3a5f,#2c3e6e)',color:'#fff',cursor:'pointer',fontSize:12,fontWeight:600 }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Chart Drill-down Modal ───────────────────────────────────────── */}
      {chartModal.open && (
        <div style={{ position:'fixed',inset:0,background:'rgba(15,23,42,0.55)',backdropFilter:'blur(4px)',zIndex:1300,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px' }}
          onClick={() => setChartModal({open:false,month:'',type:'',data:[]})}>
          <div style={{ background:'#fff',borderRadius:16,width:'100%',maxWidth:580,maxHeight:'78vh',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.22)',overflow:'hidden' }}
            onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ background:chartModal.type==='Sales'?'linear-gradient(135deg,#1e3a5f,#2c3e6e)':'linear-gradient(135deg,#92400e,#d97706)',padding:'14px 18px',display:'flex',alignItems:'center',justifyContent:'space-between' }}>
              <div>
                <div style={{ color:'#fff',fontWeight:700,fontSize:15 }}>{chartModal.type} — {chartModal.month}</div>
                <div style={{ color:'rgba(255,255,255,0.65)',fontSize:11,marginTop:2 }}>
                  {chartModal.data.length} bills · Total: {fmt(chartModal.data.reduce((s,r)=>s+(parseFloat(r.TotalAmount)||0),0))}
                </div>
              </div>
              <button onClick={() => setChartModal({open:false,month:'',type:'',data:[]})}
                style={{ background:'rgba(255,255,255,0.15)',border:'none',borderRadius:8,width:28,height:28,cursor:'pointer',color:'#fff',fontSize:16,display:'flex',alignItems:'center',justifyContent:'center' }}>×</button>
            </div>
            {/* Table */}
            <div style={{ overflowY:'auto',flex:1 }}>
              {chartModal.data.length===0
                ? <div style={{ padding:40,textAlign:'center',color:'#94a3b8',fontSize:13 }}>No records for {chartModal.month}</div>
                : <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12 }}>
                    <thead>
                      <tr style={{ background:'#f8fafc',position:'sticky',top:0,zIndex:1 }}>
                        {['#',chartModal.type==='Sales'?'Customer':'Vendor','Bill No','Date','Amount'].map((h,i)=>(
                          <th key={i} style={{ padding:'8px 12px',textAlign:i===4?'right':'left',color:'#94a3b8',fontWeight:600,fontSize:11,textTransform:'uppercase',letterSpacing:0.5,borderBottom:'1px solid #e2e8f0',whiteSpace:'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {chartModal.data.map((row,idx)=>(
                        <tr key={idx} style={{ borderBottom:'1px solid #f1f5f9' }}
                          onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'}
                          onMouseLeave={e=>e.currentTarget.style.background=''}>
                          <td style={{ padding:'7px 12px',color:'#cbd5e1',fontSize:11,width:28 }}>{idx+1}</td>
                          <td style={{ padding:'7px 12px',color:'#1e293b',fontWeight:600,maxWidth:160,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>
                            {row.ClientName||row.CustomerName||row.CistomerName||row.MasterName||'-'}
                          </td>
                          <td style={{ padding:'7px 12px',color:'#475569',fontFamily:'monospace',fontSize:12 }}>
                            {(row.BillPrefix||'')+( row.BillNumber||'-')}
                          </td>
                          <td style={{ padding:'7px 12px',color:'#64748b',whiteSpace:'nowrap' }}>
                            {row.BillDate?row.BillDate.split('T')[0]:'-'}
                          </td>
                          <td style={{ padding:'7px 12px',textAlign:'right',color:'#0f172a',fontWeight:700 }}>
                            {fmt(row.TotalAmount||0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background:'#f1f5f9',borderTop:'2px solid #e2e8f0' }}>
                        <td colSpan={4} style={{ padding:'8px 12px',fontWeight:700,color:'#1e293b',fontSize:12 }}>Total</td>
                        <td style={{ padding:'8px 12px',textAlign:'right',fontWeight:800,color:'#1e293b',fontSize:13 }}>
                          {fmt(chartModal.data.reduce((s,r)=>s+(parseFloat(r.TotalAmount)||0),0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
              }
            </div>
            <div style={{ padding:'10px 16px',borderTop:'1px solid #f0f0f0',display:'flex',justifyContent:'flex-end',background:'#fafafa' }}>
              <button onClick={() => setChartModal({open:false,month:'',type:'',data:[]})}
                style={{ padding:'6px 16px',borderRadius:8,border:'none',background:chartModal.type==='Sales'?'linear-gradient(135deg,#1e3a5f,#2c3e6e)':'linear-gradient(135deg,#92400e,#d97706)',color:'#fff',cursor:'pointer',fontSize:12,fontWeight:600 }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );};

export default Home;
